import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import 'react-toastify/dist/ReactToastify.css';
import { userAppData } from '../MainApp.js';
import { FileDropzone, SubmitButton } from '../common/formComponents.js';
import useS3FileUpload from '../hooks/useS3FileUpload.js';
import { createFormSubmission, getFormDefinition } from './formsUserApi.js';
import { getFormIcon } from './formIcons.js';
import ImageMarkupQuestion from './ImageMarkupQuestion.js';
import { createMarkedImageFile, fetchSourceImageBlob } from './imageMarkupUtils.js';
import '../common/requestForm.css';
import './forms.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };
// This project uses the react-dropzone API that expects a comma-separated
// accept string rather than the object format used by newer releases.
const EMAIL_ATTACHMENT_ACCEPT = '.png,.jpg,.jpeg,.heic,.pdf';

/** Return marker arrays while accepting responses created by the single-marker UI. */
function getImageSelectionMarkers(selection) {
    if (Array.isArray(selection?.markers)) return selection.markers;
    return selection?.marker ? [selection.marker] : [];
}

/** Renders one authorised custom form definition in its stored question order. */
export default function FormViewer() {
    const navigate = useNavigate();
    const { formId } = useParams();
    const authorised = Number(userAppData.formsData?.perms ?? 0) === 1;
    const [loading, setLoading] = useState(true);
    const [formDefinition, setFormDefinition] = useState(null);
    const [answers, setAnswers] = useState({});
    const [fileAnswers, setFileAnswers] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('');
    const { uploadFiles } = useS3FileUpload('forms');

    useEffect(() => {
        if (!authorised || !formId) return;
        async function loadForm() {
            setLoading(true);
            try {
                setFormDefinition(await getFormDefinition(formId));
            } catch (error) {
                console.error('Failed to load form definition', error);
                toast.error('❌ This form could not be loaded or you no longer have access.', TOAST_CONFIG);
            } finally {
                setLoading(false);
            }
        }
        loadForm();
    }, [authorised, formId]);

    const FormIcon = useMemo(
        () => getFormIcon(formDefinition?.icon),
        [formDefinition?.icon]
    );

    if (!authorised) return <Navigate to="/app" replace />;

    function updateAnswer(questionId, value) {
        setAnswers((current) => ({ ...current, [questionId]: value }));
    }

    function updateFileAnswer(questionId, files) {
        setFileAnswers((current) => ({ ...current, [questionId]: files }));
    }

    /** Returns the labels of required questions that have not been completed. */
    function findMissingRequiredQuestions() {
        return (formDefinition?.questions || []).reduce((missing, question, index) => {
            if (question.required !== true) return missing;
            const questionId = String(question.questionId || `question-${index}`);
            const questionType = String(question.type || 'TEXT').toUpperCase();
            const value = questionType === 'FILE' ? fileAnswers[questionId] : answers[questionId];
            const markupSelections = questionType === 'IMAGE_MARKUP' && value && typeof value === 'object'
                ? Object.values(value)
                : [];
            const incomplete = questionType === 'FILE'
                ? !Array.isArray(value) || value.length === 0
                : questionType === 'IMAGE_MARKUP'
                    ? markupSelections.length === 0 || markupSelections.some((selection) => getImageSelectionMarkers(selection).length === 0)
                : questionType === 'CHECKBOX'
                    ? value !== true
                    : String(value ?? '').trim() === '';
            if (incomplete) missing.push(question.label || `Question ${index + 1}`);
            return missing;
        }, []);
    }

    /** Return selected image-marker questions that still need a marker placement. */
    function findIncompleteImageMarkupQuestions() {
        return (formDefinition?.questions || []).reduce((incomplete, question, index) => {
            if (String(question.type || '').toUpperCase() !== 'IMAGE_MARKUP') return incomplete;
            const questionId = String(question.questionId || `question-${index}`);
            const selections = Object.values(answers[questionId] || {});
            if (selections.some((selection) => getImageSelectionMarkers(selection).length === 0)) {
                incomplete.push(question.label || `Question ${index + 1}`);
            }
            return incomplete;
        }, []);
    }

    /** Uploads file answers first, then sends their secure file references with all other answers. */
    async function handleSubmit(event) {
        event.preventDefault();
        if (isSubmitting) return;

        const missingQuestions = findMissingRequiredQuestions();
        if (missingQuestions.length > 0) {
            toast.error(`❌ Please complete:\n${missingQuestions.join('\n')}`, {
                ...TOAST_CONFIG,
                style: { whiteSpace: 'pre-line' },
            });
            return;
        }
        const incompleteImageQuestions = findIncompleteImageMarkupQuestions();
        if (incompleteImageQuestions.length > 0) {
            toast.error(`❌ Place a marker on every selected image for:\n${incompleteImageQuestions.join('\n')}`, {
                ...TOAST_CONFIG,
                style: { whiteSpace: 'pre-line' },
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const questions = formDefinition.questions || [];
            const questionsWithFiles = questions.filter((question, index) => {
                const questionId = String(question.questionId || `question-${index}`);
                const questionType = String(question.type || '').toUpperCase();
                return (questionType === 'FILE' && (fileAnswers[questionId] || []).length > 0)
                    || (questionType === 'IMAGE_MARKUP' && Object.keys(answers[questionId] || {}).length > 0);
            });
            const uploadGroupId = questionsWithFiles.length > 0 ? uuidv4() : null;
            const submissionAnswers = { ...answers };

            if (uploadGroupId) {
                setSubmitStatus('Uploading and security checking file details...');
                for (const question of questionsWithFiles) {
                    const questionId = String(question.questionId);
                    const questionType = String(question.type || '').toUpperCase();
                    let filesToUpload = fileAnswers[questionId] || [];
                    if (questionType === 'IMAGE_MARKUP') {
                        setSubmitStatus('Preparing marked location images...');
                        const imageDefinitions = question.imageMarkupSettings?.images || [];
                        filesToUpload = [];
                        for (const imageDefinition of imageDefinitions) {
                            const selection = answers[questionId]?.[imageDefinition.imageId];
                            const markers = getImageSelectionMarkers(selection);
                            if (markers.length === 0) continue;
                            const sourceBlob = await fetchSourceImageBlob(formId, imageDefinition.fileId);
                            filesToUpload.push(await createMarkedImageFile(
                                sourceBlob,
                                markers,
                                imageDefinition.name,
                                imageDefinition.imageId
                            ));
                        }
                    }
                    submissionAnswers[questionId] = await uploadFiles(
                        filesToUpload,
                        userAppData.cogId,
                        { uploadGroupId, formId, questionId }
                    );
                }
            }

            setSubmitStatus('Saving your form response...');
            const response = await createFormSubmission({
                formId,
                uploadGroupId,
                answers: submissionAnswers,
            });
            const scanPending = response.status === 'awaiting_file_scan';
            toast.success(
                scanPending
                    ? '✅ Form submitted. Attached files are still being security scanned.'
                    : '✅ Form submitted successfully.',
                TOAST_CONFIG
            );
            setAnswers({});
            setFileAnswers({});
            navigate('/app/forms');
        } catch (error) {
            console.error('Form submission failed', error);
            toast.error(`❌ ${error?.message || 'The form could not be submitted. Please try again.'}`, TOAST_CONFIG);
        } finally {
            setIsSubmitting(false);
            setSubmitStatus('');
        }
    }

    function renderQuestion(question, index) {
        const questionId = String(question.questionId || `question-${index}`);
        const label = `${index + 1}. ${question.label || 'Question'}`;
        const commonInputProps = {
            id: questionId,
            required: question.required === true,
            disabled: isSubmitting,
            value: answers[questionId] ?? '',
            onChange: (event) => updateAnswer(questionId, event.target.value),
        };

        if (question.type === 'FILE') {
            const settings = question.fileSettings || {};
            const restrictedTypes = Array.isArray(settings.allowedExtensions)
                && settings.allowedExtensions.length > 0;
            return <div className="forms-viewer-file-question" key={questionId}>
                {question.description && <p className="forms-viewer-file-description">{question.description}</p>}
                <FileDropzone
                filesState={fileAnswers[questionId] || []}
                fileStateSet={(files) => updateFileAnswer(questionId, files)}
                maxFiles={Number(settings.maxFiles) || 10}
                maxFileSizeMB={Number(settings.maxFileSizeMB) || 50}
                maxTotalSizeMB={Number(settings.maxTotalSizeMB) || 50}
                accept={restrictedTypes ? EMAIL_ATTACHMENT_ACCEPT : undefined}
                disabled={isSubmitting}
                labelText={`${label}${question.required === true ? ' (Required)' : ''}`}
                noteText="Files will be security scanned when the form is submitted."
                emptyText={question.required ? 'No files selected. This question is required.' : 'No files selected.'}
                fullWidth
                />
            </div>;
        }

        if (question.type === 'IMAGE_MARKUP') {
            return <div className="forms-viewer-image-question" key={questionId}>
                <div className="forms-viewer-image-label"><span>{label}{question.required === true && <em> Required</em>}</span>{question.description && <small>{question.description}</small>}</div>
                <ImageMarkupQuestion
                    formId={formId}
                    question={question}
                    value={answers[questionId] || {}}
                    onChange={(value) => updateAnswer(questionId, value)}
                    disabled={isSubmitting}
                />
            </div>;
        }

        return <label className="forms-viewer-field" htmlFor={questionId} key={questionId}>
            <span>{label}{question.required === true && <em> Required</em>}</span>
            {question.description && <small>{question.description}</small>}
            {question.type === 'TEXTAREA' && <textarea {...commonInputProps} rows={5} />}
            {question.type === 'DROPDOWN' && <select {...commonInputProps}><option value="">Select an option</option>{(question.options || []).map((option) => <option key={option} value={option}>{option}</option>)}</select>}
            {question.type === 'CHECKBOX' && <span className="forms-viewer-checkbox"><input id={questionId} type="checkbox" required={question.required === true} disabled={isSubmitting} checked={answers[questionId] === true} onChange={(event) => updateAnswer(questionId, event.target.checked)} /><span>Yes</span></span>}
            {question.type === 'APPROVAL' && <span className="forms-viewer-approval" role="radiogroup" aria-label={question.label}>{[
                ['approved', question.approvalSettings?.confirmLabel || 'Approve'],
                ['denied', question.approvalSettings?.denyLabel || 'Deny'],
            ].map(([value, text]) => <label key={value}><input type="radio" name={questionId} value={value} required={question.required === true} disabled={isSubmitting} checked={answers[questionId] === value} onChange={(event) => updateAnswer(questionId, event.target.value)} /><span>{text}</span></label>)}</span>}
            {!['TEXTAREA', 'DROPDOWN', 'CHECKBOX', 'APPROVAL'].includes(question.type) && <input {...commonInputProps} type={{ NUMBER: 'number', EMAIL: 'email', DATE: 'date', TIME: 'time', DATETIME: 'datetime-local' }[question.type] || 'text'} />}
        </label>;
    }

    if (loading) return <div className="main_div"><main className="request-form-shell"><section className="request-form-section"><p>Loading form...</p></section></main></div>;
    if (!formDefinition) return <div className="main_div"><main className="request-form-shell"><section className="request-form-section"><button type="button" className="forms-back-button" onClick={() => navigate('/app/forms')}>Back to Forms</button><p>This form is unavailable.</p></section></main></div>;

    return <div className="main_div"><main className="request-form-shell">
        <section className="request-form-section forms-viewer-header">
            <button type="button" className="forms-back-button" onClick={() => navigate('/app/forms')}>Back to Forms</button>
            <div className="forms-viewer-title-row"><div className="forms-viewer-title-icon"><FormIcon aria-hidden="true" /></div><div><h1 className="request-form-page-title">{formDefinition.name}</h1>{formDefinition.description && <p className="request-form-section-subheading">{formDefinition.description}</p>}</div></div>
        </section>
        <form onSubmit={handleSubmit}>
            <section className="request-form-section forms-viewer-questions">
                <h2 className="request-form-section-title">Form questions</h2>
                {(formDefinition.questions || []).map(renderQuestion)}
            </section>
            <section className="request-form-section forms-viewer-submit-section">
                <SubmitButton
                    text="Submit form"
                    loadingText={submitStatus || 'Submitting...'}
                    isLoading={isSubmitting}
                />
            </section>
        </form>
    </main></div>;
}
